import {
  Box,
  Grid2,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { InfiniteHitsProvided } from 'react-instantsearch-core'
import {
  Configure,
  connectInfiniteHits,
  InstantSearch,
} from 'react-instantsearch-dom'
import { Fade, IconButton, Icons, TextLink } from 'utoniq-ui'

import {
  ArtistStoreCollectionBoxFragment,
  Collection,
  MagazineItemFragment,
  Maybe,
  RecommendCollectionFragment,
  StoreItemFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenu, SideMenuProps } from '@/components/layouts'
import {
  ArtistStoreCollectionBox,
  ArtistStoreCollectionBoxSkeleton,
  PointDisplayPurchase,
  StoreItemArea,
  StoreItemAreaTemporary,
  StoreItemAreaTemporarySkeleton,
} from '@/components/organisms'
import {
  HorizontalList,
  ScrollFetcher,
} from '@/components/organisms/functionals'
import { StoreStateResults } from '@/components/pages/StoreRequestProvidableItemSearch/template'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { App } from '@/core/native'
import * as algolia from '@/lib/algolia'

import { SearchForm } from './SearchForm'

export * from './fragments'

export type State = {
  artistId: string
  point: string
  userLoggedInId: string
  loading: boolean
  isLoggedIn: boolean
  collections: Maybe<{ edges: { node: ArtistStoreCollectionBoxFragment }[] }>
  liveStreamId: string | undefined
  user: Maybe<SideMenuProps['user']>
  followArtists: Maybe<SideMenuProps['followArtists']>
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  isArtist: Maybe<SideMenuProps['isArtist']>
  newMagazines: MagazineItemFragment[]
  newCollections: (RecommendCollectionFragment['collection'] &
    Pick<Collection, 'publishedItemCount'>)[]
}

type CollectionCellProps = {
  artistId: State['artistId']
  loading: State['loading']
  collections: State['collections']
}

type StoreItemsCellProps = {
  items: StoreItemFragment[]
  artistId: string
  userLoggedInId: string
  hasNextPage: boolean
  onFilter: (value?: string) => void
  onFetchMore: () => Promise<void>
  onSelectItem: () => void
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

const client = algolia.client()

export const ArtistStoreTemplate: Template<State, Action> = ({ state }) => {
  const { t } = useTranslation('artistStore')
  const { analytics } = useAnalytics()
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)
  const itemResultRef = useRef<{ items: StoreItemFragment[]; page: number }>({
    items: [],
    page: 1,
  })
  const publishedAtTimestampRef = useRef<number>(DateTime.nowUnixMilliseconds())
  const [instantSearchState, setInstantSearchState] = useState({
    searchState: {},
    indexName: algolia.indexes.items,
  })
  const { routerNames, router, routerPages, generatePath } = useRouter()

  const handleOnSearchStateChange = useCallback((newState: object) => {
    setInstantSearchState(prevState => ({
      indexName: prevState.indexName,
      searchState: newState,
    }))
  }, [])

  const handleFilter = useCallback((order?: string) => {
    switch (order) {
      case 'price_desc':
        return setInstantSearchState(prevState => ({
          indexName: algolia.indexes.itemsPriceDesc,
          searchState: { ...prevState.searchState, page: 1 },
        }))
      case 'price_asc':
        return setInstantSearchState(prevState => ({
          indexName: algolia.indexes.itemsPriceAsc,
          searchState: { ...prevState.searchState, page: 1 },
        }))
      default:
        return setInstantSearchState(prevState => ({
          indexName: algolia.indexes.items,
          searchState: { ...prevState.searchState, page: 1 },
        }))
    }
  }, [])

  const handleOnCompleted = useCallback(
    (items: StoreItemFragment[], { page }: { page: number }) => {
      itemResultRef.current = { items, page }
      analytics?.track('view_item_list', {
        item_list_id: 'artist_store_list',
        item_list_name: 'Artist Store Items',
        items: items.map(item => ({
          item_id: item.id,
          item_name: item.title,
          item_category: 'store_item',
          item_category2: item.type,
          item_category3: App.getPlatform(),
          item_category4: page,
          item_category5: state.artistId,
          price: item.price,
        })),
      })
    },
    [analytics, state.artistId]
  )

  const handleSelectItem = useCallback(() => {
    analytics?.track('select_item', {
      item_list_id: `artist_store_list_${state.artistId}`,
      item_list_name: 'Artist Store Items',
      items: itemResultRef.current.items.map(item => ({
        item_id: item.id,
        item_name: item.title,
        item_category: 'store_item',
        item_category2: item.type,
        item_category3: App.getPlatform(),
        item_category4: itemResultRef.current.page,
        item_category5: state.artistId,
        price: item.price,
      })),
    })
  }, [analytics, state.artistId])

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ArtistStore} />
      <BaseLayout
        navigation={{
          type: 'artist',
          props: {
            isLive: !!state.liveStreamId,
            hasManagementTask: !!state.user?.hasManagementTask,
            unreadNotificationCount: state.user?.unreadNotificationCount,
          },
        }}
        header={{
          title: t('title'),
          left: config.applicationType === 'organization' && (
            <IconButton.AvatarNoImage
              edge="start"
              onClick={() => setOpenSideMenu(!openSideMenu)}
              badge={
                state.user?.hasUnappliedDeliveryItems ||
                state.user?.hasWaitingPaymentItems ||
                !!state.user?.unreadNotificationCount
              }
              color="icon.line"
              bgcolor="icon.background"
            />
          ),
          right: state.isLoggedIn && !!config.modules.prepaidPoint && (
            <PointDisplayPurchase point={state.point} />
          ),
        }}
      >
        <InstantSearch
          indexName={instantSearchState.indexName}
          searchClient={client}
          refresh
          searchState={instantSearchState.searchState}
          onSearchStateChange={handleOnSearchStateChange}
        >
          <Configure
            hitsPerPage={10}
            filters={algolia.filters.itemStoreArtist(
              state.artistId,
              publishedAtTimestampRef.current
            )}
          />
          <SearchForm
            onSubmit={async ({ keyword }) =>
              router.push({
                pathname: generatePath(
                  routerPages.StoreRequestProvidableItemSearch,
                  { artistId: state.artistId }
                ),
                search: `?keyword=${keyword}`,
              })
            }
          />
          <CollectionCell
            loading={state.loading}
            artistId={state.artistId}
            collections={state.collections}
          />
          <RecentlyStoreViewedItemsLabel
            artistId={state.artistId}
            loading={state.loading}
            newMagazines={state.newMagazines}
          />
          <RecentlyViewedCollectionsCell
            artistId={state.artistId}
            loading={state.loading}
            newCollections={state.newCollections}
          />
          <StoreItemsInfiniteHits
            artistId={state.artistId}
            userLoggedInId={state.userLoggedInId}
            onFilter={handleFilter}
            onSelectItem={handleSelectItem}
          />
          <StoreStateResults onCompleted={handleOnCompleted} />
        </InstantSearch>
        <SideMenu
          followArtists={state.followArtists}
          memberArtists={state.memberArtists}
          open={openSideMenu}
          onClose={() => setOpenSideMenu(false)}
          user={state.user}
          isArtist={state.isArtist}
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const CollectionCell: Cell<CollectionCellProps> = ({
  artistId,
  loading,
  collections,
}) => {
  const { t } = useTranslation('artistStore')
  const { generatePath, routerPages } = useRouter()

  if (!collections?.edges.length) return null

  return (
    <CellFrame sx={{ px: 5 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ py: 2 }}>
        <Typography variant="h4">
          {t('collectionTitle', 'コレクションから探す')}
        </Typography>
        <TextLink
          variant="h5"
          underline="none"
          href={generatePath(routerPages.StoreCollectionsSelect, { artistId })}
          sx={{ color: 'primary.main' }}
        >
          {t('toList', '一覧へ')}
        </TextLink>
      </Stack>
      <ScrollFetcher
        component={Grid2}
        componentProps={{ spacing: 3, container: true }}
        itemComponent={Grid2}
        itemComponentProps={{ size: { xs: 6, md: 6 } }}
        nodes={collections?.edges.map(({ node }, index) => (
          <ArtistStoreCollectionBox
            conductorName="artistStore"
            key={index}
            collection={node}
          />
        ))}
        loading={loading}
        hasNextPage={false}
        onFetchMore={async () => {}}
        loadingComponent={
          <Grid2 container spacing={3} sx={{ py: 2 }}>
            {Array.from(Array(6).keys()).map(key => (
              <Grid2 size={{ xs: 6, md: 6 }} key={key}>
                <Skeleton variant="rectangular" width="100%" height={166} />
              </Grid2>
            ))}
          </Grid2>
        }
      />
    </CellFrame>
  )
}

const StoreItemsInfiniteHits = connectInfiniteHits<
  InfiniteHitsProvided<Omit<StoreItemFragment, 'id'> & { objectID: string }> & {
    artistId: StoreItemsCellProps['artistId']
    userLoggedInId: StoreItemsCellProps['userLoggedInId']
    onFilter: StoreItemsCellProps['onFilter']
    onSelectItem: StoreItemsCellProps['onSelectItem']
  },
  {}
>(
  ({
    hits,
    hasMore,
    artistId,
    userLoggedInId,
    onFilter,
    onSelectItem,
    refineNext,
  }) => {
    const items = hits.map(({ objectID, ...item }) => ({
      id: objectID,
      ...item,
    }))
    return (
      <StoreItemsCell
        items={items}
        artistId={artistId}
        userLoggedInId={userLoggedInId}
        onFilter={onFilter}
        onSelectItem={onSelectItem}
        hasNextPage={hasMore}
        onFetchMore={async () => refineNext()}
      />
    )
  }
)

const StoreItemsCell: Cell<StoreItemsCellProps> = ({
  artistId,
  userLoggedInId,
  items,
  hasNextPage,
  onFilter,
  onFetchMore,
  onSelectItem,
}) => {
  const { t } = useTranslation('artistStore')
  const params = new URLSearchParams(window.location.search)
  const sort = params.get('sort')
  const [selectedOption, setSelectedOption] = useState<undefined | string>(
    sort ?? undefined
  )
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const options = [
    { value: undefined, label: t('newArrivalOrder') },
    { value: 'price_asc', label: t('cheapestFirst') },
    { value: 'price_desc', label: t('highestFirst') },
  ]

  const handleClick = useCallback(
    (newVal: string | undefined) => {
      setAnchorEl(null)
      setSelectedOption(newVal)
      onFilter(newVal)
    },
    [onFilter]
  )
  useEffect(() => {
    onFilter(sort ?? undefined)
  }, [sort])

  useEffect(() => {
    if (selectedOption) {
      const UrlParams = `?sort=${selectedOption}`
      // React Routerのhistoryを使うとソート順を変えた際に、スクロール位置が一番上になるため、JavaScriptで直接historyを利用している
      window.history.replaceState(null, '', UrlParams)
    } else {
      window.history.replaceState(null, '', location.pathname)
    }
  }, [selectedOption])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <CellFrame sx={{ px: 5, pb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" sx={{ py: 2 }}>
          {t('itemsForSale')}
        </Typography>
        <IconButton.Slider
          edge="end"
          size="medium"
          onClick={event => setAnchorEl(event.currentTarget)}
        />
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{ sx: { width: 200 } }}
        >
          {options.map((option, index) => (
            <MenuItem
              key={index}
              selected={option.value === selectedOption}
              onClick={() => handleClick(option.value)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </Stack>
      <ScrollFetcher
        loading={false}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        component={Grid2}
        componentProps={{ spacing: 2, container: true }}
        itemComponent={Grid2}
        itemComponentProps={{ size: { xs: 4, md: 3 } }}
        emptyMessage={<Box />}
        nodes={items?.map((item, index) => (
          <StoreItemArea
            key={index}
            item={item}
            artistId={artistId}
            onClick={onSelectItem}
            showMenberOnlyIcon={true}
          />
        ))}
      />
    </CellFrame>
  )
}

/** 最近みた商品 */
type RecentlyStoreViewedItemsLabelProps = {
  loading: boolean
  artistId: string
  newMagazines: State['newMagazines']
}
const RecentlyStoreViewedItemsLabel: Cell<RecentlyStoreViewedItemsLabelProps> =
  ({ loading, artistId, newMagazines }) => {
    const { t } = useTranslation('artistTop')
    const { routerPages, generatePath } = useRouter()

    if (!loading && !newMagazines.length) return null

    return (
      <Box sx={{ pt: 4, mb: -5 }}>
        <HorizontalList
          loading={loading}
          title={t('recentlyStoreViewedItemsLabel')}
          items={newMagazines}
          trans={t('toList')}
          listHref={generatePath(routerPages.RecentViewedItems, { artistId })}
          spacing={1}
          TitleIcon={
            <Icons.Bookmark fontSize="medium" sx={{ color: 'grey.700' }} />
          }
          LoadingComponent={StoreItemAreaTemporarySkeleton}
          renderItem={item => (
            <Fade key={item.id} in>
              <StoreItemAreaTemporary
                item={item}
                conductorName="artistTopRecentryStoreView"
              />
            </Fade>
          )}
        />
      </Box>
    )
  }

/** 閲覧履歴からのおすすめ(マガジン) */
type RecentlyViewedCollectionsCellProps = {
  loading: boolean
  newCollections: State['newCollections']
  artistId: string
}
const RecentlyViewedCollectionsCell: Cell<RecentlyViewedCollectionsCellProps> =
  ({ loading, newCollections, artistId }) => {
    const { t } = useTranslation('artistTop')
    const { routerPages, generatePath } = useRouter()

    if (!loading && !newCollections.length) return null

    return (
      <HorizontalList
        loading={loading}
        title={t('recentlyViewedCollectionsLabel')}
        items={newCollections}
        trans={t('toList')}
        listHref={generatePath(routerPages.RecommendCollections, { artistId })}
        TitleIcon={<Icons.Time fontSize="medium" sx={{ color: 'grey.700' }} />}
        LoadingComponent={() => (
          <ArtistStoreCollectionBoxSkeleton width={160} />
        )}
        renderItem={collection => (
          <Fade key={collection.id} in>
            <ArtistStoreCollectionBox
              conductorName="artistTopRecentryViewCollection"
              width={160}
              collection={collection}
            />
          </Fade>
        )}
      />
    )
  }
